import { useEffect, useState } from "preact/hooks";
import {
  adminGetOutreachTokenEndpoint,
  adminGetUserStatsEndpoint,
  adminListInviteCodesEndpoint,
  adminListUsersEndpoint,
  adminSendBuilderOutreachEndpoint,
  adminSendUserUpdateEndpoint,
  apiClient,
  apiUrl,
  superAdminEmail,
} from "../../constants.ts";
import { useAuth } from "./db.ts";
import {
  Button,
  CodeBlock,
  CopyButton,
  inputBaseClass,
  Spinner,
  TokenDisplay,
} from "./components.tsx";
import { usePageMeta } from "./pageMeta.ts";
import { toast } from "react-hot-toast";

interface SupergreenAccountInfo {
  id: string;
  phoneNumber: string;
  isConnected?: boolean;
}

interface BotInfo {
  id: string;
  name: string;
  telegramUsername?: string;
  agentRunCount?: number;
  llmRunCount?: number;
  incomingMessageCount?: number;
  promptTokenCount?: number;
  candidatesTokenCount?: number;
  thoughtsTokenCount?: number;
  cachedContentTokenCount?: number;
  imageGenerationCount?: number;
  lastInteraction?: number;
  supergreenWaAccounts?: SupergreenAccountInfo[];
}

interface RefStats {
  visitsCount: number;
  signupsCount: number;
  conversionsCount: number;
  earnings: number;
}

interface UserIdentity {
  id: string;
  email?: string;
  phone?: string;
  telegramId?: string;
}

interface UserStats {
  email?: string;
  phone?: string;
  telegramId?: string;
  tier: string;
  isPaid: boolean;
  usedRuns: number;
  limitRuns: number;
  isOverQuota: boolean;
  signupTimestamp?: number;
  bots: BotInfo[];
  refStats?: RefStats;
  referredBy?: UserIdentity;
}

interface AdminUserSummary {
  id: string;
  email?: string;
  phone?: string;
  telegramId?: string;
  tier: string;
  isPaid: boolean;
  signupTimestamp?: number;
  lastVisit?: number;
  agentRunCount?: number;
  llmRunCount?: number;
  refStats: RefStats;
  hasActiveRef: boolean;
  botsCount: number;
}

export interface AdminInviteCode {
  id: string;
  code: string;
  createdAt: number;
  usedAt?: number;
  usedByEmail?: string;
  generatedByEmail?: string;
}

export type SortColumn =
  | "default"
  | "user"
  | "tier"
  | "refSignups"
  | "refEarnings"
  | "botsCount"
  | "lastActive";

export type SortDirection = "asc" | "desc";

const getUserLabel = (u: UserIdentity): string =>
  u.email || u.phone || (u.telegramId ? `TG: ${u.telegramId}` : u.id);

const digitsOnly = (s: string): string => s.replace(/\D/g, "");

export const filterUsers = (
  users: AdminUserSummary[],
  tableFilter: string,
): AdminUserSummary[] => {
  if (!tableFilter.trim()) return users;
  const term = tableFilter.toLowerCase();
  const phoneDigits = digitsOnly(term);
  return users.filter(
    (u) =>
      [u.email, u.phone, u.telegramId, u.tier, u.id].some(
        (field) => field && field.toLowerCase().includes(term),
      ) ||
      Boolean(
        u.phone &&
          phoneDigits.length > 0 &&
          digitsOnly(u.phone).includes(phoneDigits),
      ),
  );
};

export const sortUsers = (
  users: AdminUserSummary[],
  field: SortColumn,
  dir: SortDirection,
): AdminUserSummary[] => {
  if (field === "default") return users;
  const multiplier = dir === "asc" ? 1 : -1;
  return [...users].sort((a, b) => {
    if (field === "user") {
      return getUserLabel(a).toLowerCase().localeCompare(
        getUserLabel(b).toLowerCase(),
      ) * multiplier;
    }
    if (field === "tier") {
      const paidDiff = (a.isPaid ? 1 : 0) - (b.isPaid ? 1 : 0);
      if (paidDiff !== 0) return paidDiff * multiplier;
      return a.tier.localeCompare(b.tier) * multiplier;
    }
    if (field === "refSignups") {
      return (a.refStats.signupsCount - b.refStats.signupsCount) * multiplier;
    }
    if (field === "refEarnings") {
      return (a.refStats.earnings - b.refStats.earnings) * multiplier;
    }
    if (field === "botsCount") {
      return (a.botsCount - b.botsCount) * multiplier;
    }
    if (field === "lastActive") {
      const timeA = a.lastVisit || a.signupTimestamp || 0;
      const timeB = b.lastVisit || b.signupTimestamp || 0;
      return (timeA - timeB) * multiplier;
    }
    return 0;
  });
};

const UserStatsModal = ({
  isOpen,
  onClose,
  loading,
  error,
  stats,
}: {
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
  error: string;
  stats: UserStats | null;
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle rounded-xl shadow-2xl p-4 sm:p-6 flex flex-col gap-4 sm:gap-6 min-w-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border-subtle-light dark:border-border-subtle pb-3 sm:pb-4">
          <h2 className="text-base sm:text-lg font-bold text-text-primary-light dark:text-text-primary truncate">
            User Stats Details
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-tertiary-light dark:text-text-tertiary hover:text-text-primary-light dark:hover:text-text-primary p-1 rounded-lg transition-colors cursor-pointer shrink-0"
            aria-label="Close"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {loading && (
          <div className="py-12 flex flex-col items-center justify-center gap-3 text-text-secondary-light dark:text-text-secondary text-sm">
            <Spinner className="h-8 w-8 text-blue-500" />
            <span>Fetching user stats...</span>
          </div>
        )}

        {error && (
          <div className="p-4 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-sm border border-red-200 dark:border-red-800 break-words">
            {error}
          </div>
        )}

        {!loading && !error && stats && (
          <div className="space-y-4 sm:space-y-6 min-w-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="p-3.5 sm:p-4 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-default-light dark:border-border-default min-w-0">
                <h3 className="text-sm font-semibold text-text-secondary-light dark:text-text-secondary mb-3">
                  User Information
                </h3>
                <div className="space-y-2.5 sm:space-y-3 text-sm">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0">
                    <span className="font-semibold text-text-secondary-light dark:text-text-secondary shrink-0">
                      Email:
                    </span>
                    <span className="text-text-primary-light dark:text-text-primary font-mono select-all break-all min-w-0">
                      {stats.email || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0">
                    <span className="font-semibold text-text-secondary-light dark:text-text-secondary shrink-0">
                      Phone:
                    </span>
                    <span className="text-text-primary-light dark:text-text-primary font-mono select-all break-all min-w-0">
                      {stats.phone || "N/A"}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0">
                    <span className="font-semibold text-text-secondary-light dark:text-text-secondary shrink-0">
                      Telegram ID:
                    </span>
                    <span className="text-text-primary-light dark:text-text-primary font-mono select-all break-all min-w-0">
                      {stats.telegramId || "N/A"}
                    </span>
                  </div>
                  {stats.signupTimestamp && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0">
                      <span className="font-semibold text-text-secondary-light dark:text-text-secondary shrink-0">
                        Signed Up:
                      </span>
                      <span className="text-text-primary-light dark:text-text-primary font-mono break-all min-w-0">
                        {new Date(stats.signupTimestamp).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-2 min-w-0">
                    <span className="font-semibold text-text-secondary-light dark:text-text-secondary shrink-0">
                      Acquisition:
                    </span>
                    {stats.referredBy
                      ? (
                        <span className="font-semibold text-green-600 dark:text-green-400 min-w-0 break-words">
                          Affiliate — referred by{" "}
                          <span className="font-mono select-all break-all">
                            {getUserLabel(stats.referredBy)}
                          </span>
                        </span>
                      )
                      : (
                        <span className="text-text-primary-light dark:text-text-primary min-w-0 break-words">
                          Organic (no affiliate)
                        </span>
                      )}
                  </div>
                </div>
              </div>

              <div className="p-3.5 sm:p-4 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-default-light dark:border-border-default min-w-0">
                <h3 className="text-sm font-semibold text-text-secondary-light dark:text-text-secondary mb-3">
                  Subscription & Quota
                </h3>
                <div className="space-y-2.5 sm:space-y-3 text-sm">
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap min-w-0">
                    <span className="font-semibold text-text-secondary-light dark:text-text-secondary shrink-0">
                      Tier:
                    </span>
                    <span className="font-bold text-blue-600 dark:text-blue-400 break-all">
                      {stats.tier}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-300 font-medium shrink-0">
                      {stats.isPaid ? "Paying" : "Non-Paying"}
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0">
                    <span className="font-semibold text-text-secondary-light dark:text-text-secondary shrink-0">
                      Quota Used:
                    </span>
                    <span className="text-text-primary-light dark:text-text-primary font-mono break-all min-w-0">
                      {stats.usedRuns} / {stats.limitRuns} runs
                    </span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 min-w-0">
                    <span className="font-semibold text-text-secondary-light dark:text-text-secondary shrink-0">
                      Quota Status:
                    </span>
                    <span
                      className={`font-semibold min-w-0 break-words ${
                        stats.isOverQuota
                          ? "text-red-500 dark:text-red-400"
                          : "text-green-500 dark:text-green-400"
                      }`}
                    >
                      {stats.isOverQuota
                        ? "Over Quota (Blocked)"
                        : "Within Quota"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {stats.refStats && (
              <div className="p-3.5 sm:p-4 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-default-light dark:border-border-default min-w-0">
                <h3 className="text-sm font-semibold text-text-secondary-light dark:text-text-secondary mb-3">
                  Referral & Affiliate Stats
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm min-w-0">
                  <div className="min-w-0">
                    <span className="text-xs text-text-secondary-light dark:text-text-secondary block truncate">
                      Visits
                    </span>
                    <span className="font-bold font-mono break-all">
                      {stats.refStats.visitsCount}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs text-text-secondary-light dark:text-text-secondary block truncate">
                      Signups
                    </span>
                    <span className="font-bold font-mono break-all">
                      {stats.refStats.signupsCount}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs text-text-secondary-light dark:text-text-secondary block truncate">
                      Paying Conversions
                    </span>
                    <span className="font-bold font-mono break-all">
                      {stats.refStats.conversionsCount}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <span className="text-xs text-text-secondary-light dark:text-text-secondary block truncate">
                      Est. Monthly Earnings
                    </span>
                    <span className="font-bold font-mono text-green-600 dark:text-green-400 break-all">
                      ${stats.refStats.earnings}/mo
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="p-3.5 sm:p-4 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-default-light dark:border-border-default min-w-0">
              <h3 className="text-sm font-semibold text-text-secondary-light dark:text-text-secondary mb-3">
                User Bots ({stats.bots.length})
              </h3>
              {stats.bots.length === 0
                ? (
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary">
                    This user has no bots.
                  </p>
                )
                : (
                  <div className="divide-y divide-border-subtle-light dark:divide-border-subtle min-w-0">
                    {stats.bots.map((bot: BotInfo) => {
                      const totalTokens = (bot.promptTokenCount ?? 0) +
                        (bot.candidatesTokenCount ?? 0) +
                        (bot.thoughtsTokenCount ?? 0);
                      return (
                        <div
                          key={bot.id}
                          className="py-3.5 first:pt-0 last:pb-0 flex flex-col gap-2 min-w-0"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 min-w-0">
                            <div className="min-w-0">
                              <div className="font-medium text-sm break-words flex items-center gap-2 flex-wrap">
                                <span>{bot.name}</span>
                                {bot.telegramUsername && (
                                  <a
                                    href={`https://t.me/${bot.telegramUsername}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-blue-500 hover:underline shrink-0 break-all"
                                  >
                                    @{bot.telegramUsername}
                                  </a>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-tertiary-light dark:text-text-tertiary font-mono min-w-0 mt-0.5">
                                <span className="shrink-0">ID:</span>
                                <span className="select-all break-all min-w-0">
                                  {bot.id}
                                </span>
                              </div>
                            </div>
                          </div>

                          {bot.supergreenWaAccounts &&
                            bot.supergreenWaAccounts.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                              <span className="font-semibold text-text-secondary-light dark:text-text-secondary">
                                Supergreen WA:
                              </span>
                              {bot.supergreenWaAccounts.map((sg) => (
                                <span
                                  key={sg.id || sg.phoneNumber}
                                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded font-mono text-xs ${
                                    sg.isConnected !== false
                                      ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border border-green-300 dark:border-green-700"
                                      : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300 dark:border-amber-700"
                                  }`}
                                >
                                  <span
                                    className={`w-2 h-2 rounded-full ${
                                      sg.isConnected !== false
                                        ? "bg-green-500"
                                        : "bg-amber-500"
                                    }`}
                                  />
                                  +{sg.phoneNumber}
                                  <span className="opacity-75 text-[10px]">
                                    ({sg.isConnected !== false
                                      ? "Connected"
                                      : "Disconnected"})
                                  </span>
                                </span>
                              ))}
                            </div>
                          )}

                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-2 border-t border-border-subtle-light/50 dark:border-border-subtle/50 text-xs">
                            <div>
                              <span className="text-text-tertiary-light dark:text-text-tertiary block">
                                Agent Runs
                              </span>
                              <span className="font-mono font-medium text-text-primary-light dark:text-text-primary">
                                {(bot.agentRunCount ?? 0).toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="text-text-tertiary-light dark:text-text-tertiary block">
                                LLM Runs
                              </span>
                              <span className="font-mono font-medium text-text-primary-light dark:text-text-primary">
                                {(bot.llmRunCount ?? 0).toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="text-text-tertiary-light dark:text-text-tertiary block">
                                Token Usage
                              </span>
                              <span
                                className="font-mono font-medium text-text-primary-light dark:text-text-primary"
                                title={`Prompt: ${
                                  (bot.promptTokenCount ?? 0).toLocaleString()
                                } | Output: ${
                                  (bot.candidatesTokenCount ?? 0)
                                    .toLocaleString()
                                } | Thoughts: ${
                                  (bot.thoughtsTokenCount ?? 0).toLocaleString()
                                }`}
                              >
                                {totalTokens.toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="text-text-tertiary-light dark:text-text-tertiary block">
                                Messages
                              </span>
                              <span className="font-mono font-medium text-text-primary-light dark:text-text-primary">
                                {(bot.incomingMessageCount ?? 0)
                                  .toLocaleString()}
                              </span>
                            </div>
                            <div>
                              <span className="text-text-tertiary-light dark:text-text-tertiary block">
                                Last Interaction
                              </span>
                              <span className="font-mono font-medium text-text-primary-light dark:text-text-primary">
                                {bot.lastInteraction
                                  ? new Date(
                                    bot.lastInteraction,
                                  ).toLocaleString()
                                  : "None"}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          </div>
        )}

        <div className="flex justify-end pt-3 sm:pt-4 border-t border-border-subtle-light dark:border-border-subtle">
          <Button type="button" onClick={onClose} className="w-full sm:w-auto">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

const TableHeaderCell = ({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  className = "p-3",
}: {
  label: string;
  field: SortColumn;
  sortField: SortColumn;
  sortDirection: SortDirection;
  onSort: (field: SortColumn) => void;
  className?: string;
}) => (
  <th
    onClick={() => onSort(field)}
    className={`${className} cursor-pointer select-none hover:bg-surface-overlay-light/80 dark:hover:bg-surface-overlay/80 transition-colors`}
  >
    <div className="flex items-center gap-1">
      <span>{label}</span>
      {sortField === field && (
        <span className="text-blue-500 font-bold">
          {sortDirection === "asc" ? "▲" : "▼"}
        </span>
      )}
    </div>
  </th>
);

const agentOutreachInstructions = ({
  token,
  apiUrl,
}: {
  token: string;
  apiUrl: string;
}) =>
  `You have access to the Prompt2bot Builder AI WhatsApp Outreach API.
When you discover or qualify a prospective user or business that needs an AI bot/agent, trigger an autonomous WhatsApp outreach from Prompt2bot's Builder AI.

The Builder AI will reach out to them directly on WhatsApp, introduce itself, and interactively build their agent.

HTTP Request:
POST ${apiUrl}/api
Content-Type: application/json

Payload:
{
  "endpoint": "admin-send-builder-outreach",
  "token": "${token}",
  "payload": {
    "phone": "<PHONE_NUMBER>",
    "name": "<PROSPECT_NAME>",
    "language": "english",
    "source": "<LEAD_SOURCE>",
    "useCase": "<BUSINESS_NEED_OR_USE_CASE>",
    "context": "<OPTIONAL_ADDITIONAL_NOTES>"
  }
}

Field Descriptions:
- phone (required): Prospect's phone number with international country code (e.g. +14155552671 or +447700900077).
- name (optional): Prospect's name so the Builder AI addresses them personally.
- language: "english" (or "hebrew").
- source (optional): Where the lead came from (e.g. "Reddit r/n8n", "LinkedIn post", "Inbound form").
- useCase (required): What the prospect does and what specific automation or bot they need (e.g. "Customer support bot for Shopify store to handle order tracking and FAQs via WhatsApp").
- context (optional): Extra constraints, links, or notes.

Response:
{ "success": true, "taskId": "..." }`;

const curlOutreachExample = ({
  token,
  apiUrl,
}: {
  token: string;
  apiUrl: string;
}) =>
  `curl -X POST ${apiUrl}/api \\
  -H "Content-Type: application/json" \\
  -d '{
    "endpoint": "admin-send-builder-outreach",
    "token": "${token}",
    "payload": {
      "phone": "+14155552671",
      "name": "Alex",
      "language": "english",
      "source": "Reddit r/n8n",
      "useCase": "Wants a WhatsApp bot to automate customer inquiries and sync appointments with Google Calendar."
    }
  }'`;

export const AdminPage = () => {
  usePageMeta({
    title: "Admin Panel | prompt2bot",
    description: "Admin panel, user queries, and outreach.",
  });

  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<
    "queries" | "outreach" | "user-update" | "promo-codes"
  >("queries");

  // Invite Codes state
  const [inviteCodes, setInviteCodes] = useState<AdminInviteCode[]>([]);
  const [inviteCodesLoading, setInviteCodesLoading] = useState(false);
  const [inviteCodesError, setInviteCodesError] = useState("");
  const [showUnusedOnly, setShowUnusedOnly] = useState(true);

  // User Stats state
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stats, setStats] = useState<UserStats | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Users Table state
  const [usersList, setUsersList] = useState<AdminUserSummary[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState("");
  const [tableFilter, setTableFilter] = useState("");

  // Users Table Pagination & Sorting state
  const [sortField, setSortField] = useState<SortColumn>("default");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Outreach state
  const [outreachPhone, setOutreachPhone] = useState("");
  const [outreachName, setOutreachName] = useState("");
  const [outreachLanguage, setOutreachLanguage] = useState<
    "hebrew" | "english"
  >("hebrew");
  const [outreachSource, setOutreachSource] = useState("");
  const [outreachUseCase, setOutreachUseCase] = useState("");
  const [outreachContext, setOutreachContext] = useState("");
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [outreachError, setOutreachError] = useState("");
  const [outreachSuccess, setOutreachSuccess] = useState("");
  const [outreachToken, setOutreachToken] = useState("");
  const [outreachTokenLoading, setOutreachTokenLoading] = useState(false);
  const [outreachTokenError, setOutreachTokenError] = useState("");

  const fetchOutreachToken = async () => {
    if (!user || user.email !== superAdminEmail) return;
    setOutreachTokenLoading(true);
    setOutreachTokenError("");
    try {
      const res = await apiClient({
        endpoint: adminGetOutreachTokenEndpoint,
        token: user.refresh_token,
        payload: {},
      });
      if (res.success) {
        setOutreachToken(res.token);
      } else {
        setOutreachTokenError(res.error || "Failed to fetch outreach token");
      }
    } catch (err) {
      setOutreachTokenError(
        err instanceof Error ? err.message : "Failed to fetch outreach token",
      );
    } finally {
      setOutreachTokenLoading(false);
    }
  };

  // User Update state
  const [updateContext, setUpdateContext] = useState("");
  const [sendToHebrewGroup, setSendToHebrewGroup] = useState(true);
  const [sendToEnglishGroup, setSendToEnglishGroup] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [updateSuccess, setUpdateSuccess] = useState("");

  const fetchUsers = async () => {
    if (!user || user.email !== superAdminEmail) return;
    setUsersLoading(true);
    setUsersError("");
    try {
      const res = await apiClient({
        endpoint: adminListUsersEndpoint,
        token: user.refresh_token,
        payload: {},
      });
      if (res.success) {
        setUsersList(res.users);
      } else {
        setUsersError(res.error || "Failed to fetch users list");
      }
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchInviteCodes = async () => {
    if (!user || user.email !== superAdminEmail) return;
    setInviteCodesLoading(true);
    setInviteCodesError("");
    try {
      const res = await apiClient({
        endpoint: adminListInviteCodesEndpoint,
        token: user.refresh_token,
        payload: {},
      });
      if (res.success) {
        setInviteCodes(res.codes);
      } else {
        setInviteCodesError(res.error || "Failed to load invite codes");
      }
    } catch (e) {
      setInviteCodesError(e instanceof Error ? e.message : String(e));
    } finally {
      setInviteCodesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "promo-codes" && user?.email === superAdminEmail) {
      fetchInviteCodes();
    }
  }, [activeTab, user?.email]);

  useEffect(() => {
    if (activeTab === "queries" && user?.email === superAdminEmail) {
      fetchUsers();
    }
  }, [activeTab, user?.email]);

  useEffect(() => {
    if (
      activeTab === "outreach" &&
      user?.email === superAdminEmail &&
      !outreachToken &&
      !outreachTokenLoading
    ) {
      fetchOutreachToken();
    }
  }, [activeTab, user?.email, outreachToken, outreachTokenLoading]);

  if (!user || user.email !== superAdminEmail) {
    return (
      <div className="max-w-md mx-auto my-10 p-6 rounded-lg shadow-md bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle text-center text-text-primary-light dark:text-text-primary">
        <h1 className="text-xl font-bold mb-4">Access Denied</h1>
        <p className="text-sm text-text-secondary-light dark:text-text-secondary">
          This page is only accessible to system administrators.
        </p>
      </div>
    );
  }

  const lookupUserByQuery = async (searchTarget: string) => {
    const trimmed = searchTarget.trim();
    if (!trimmed) return;
    setLoading(true);
    setError("");
    setStats(null);
    try {
      const res = await apiClient({
        endpoint: adminGetUserStatsEndpoint,
        token: user.refresh_token,
        payload: { query: trimmed },
      });
      if (res.success) {
        setStats(res.stats);
      } else {
        setError(res.error || "Failed to fetch user stats");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: Event) => {
    e.preventDefault();
    setIsModalOpen(true);
    lookupUserByQuery(query);
  };

  const handleSort = (field: SortColumn) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection(field === "user" ? "asc" : "desc");
    }
    setPage(1);
  };

  const handleSendOutreach = async (e: Event) => {
    e.preventDefault();
    const phone = outreachPhone.trim();
    const useCase = outreachUseCase.trim();
    const context = outreachContext.trim();
    if (!phone || (!useCase && !context)) return;
    setOutreachLoading(true);
    setOutreachError("");
    setOutreachSuccess("");

    const promise = apiClient({
      endpoint: adminSendBuilderOutreachEndpoint,
      token: user.refresh_token,
      payload: {
        phone,
        name: outreachName.trim() || undefined,
        language: outreachLanguage,
        source: outreachSource.trim() || undefined,
        useCase: useCase || undefined,
        context: context || undefined,
      },
    }).then((res) => {
      if (res.success) {
        setOutreachSuccess(
          `Builder AI outreach initiated successfully! Task ID: ${res.taskId}`,
        );
        setOutreachPhone("");
        setOutreachName("");
        setOutreachSource("");
        setOutreachUseCase("");
        setOutreachContext("");
        return res;
      }
      throw new Error(res.error || "Failed to initiate outreach");
    });

    try {
      await toast.promise(promise, {
        loading: "Sending Builder AI outreach...",
        success: "Builder AI outreach sent on WhatsApp!",
        error: (err) =>
          err instanceof Error ? err.message : "Failed to send outreach",
      });
    } catch (err) {
      setOutreachError(
        err instanceof Error ? err.message : "An error occurred",
      );
    } finally {
      setOutreachLoading(false);
    }
  };

  const handleSendUserUpdate = async (e: Event) => {
    e.preventDefault();
    if (!updateContext.trim()) return;
    if (!sendToHebrewGroup && !sendToEnglishGroup) {
      setUpdateError("Please select at least one target group.");
      return;
    }
    setUpdateLoading(true);
    setUpdateError("");
    setUpdateSuccess("");

    const promise = apiClient({
      endpoint: adminSendUserUpdateEndpoint,
      token: user.refresh_token,
      payload: {
        context: updateContext.trim(),
        sendToHebrewGroup,
        sendToEnglishGroup,
      },
    }).then((res) => {
      if (res.success) {
        setUpdateSuccess(
          `User update scheduled successfully! Task IDs: ${
            res.taskIds.join(", ")
          }`,
        );
        setUpdateContext("");
        return res;
      }
      throw new Error(res.error || "Failed to trigger user update");
    });

    try {
      await toast.promise(promise, {
        loading: "Scheduling Builder AI user update...",
        success: "User update scheduled for community groups!",
        error: (err) =>
          err instanceof Error ? err.message : "Failed to send update",
      });
    } catch (err) {
      setUpdateError(
        err instanceof Error ? err.message : "An error occurred",
      );
    } finally {
      setUpdateLoading(false);
    }
  };

  const filteredUsers = filterUsers(usersList, tableFilter);

  const sortedUsers = sortUsers(filteredUsers, sortField, sortDirection);
  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const unusedCodes = inviteCodes.filter((c) => !c.usedAt);
  const redeemedCodes = inviteCodes.filter((c) => Boolean(c.usedAt));
  const displayedCodes = showUnusedOnly ? unusedCodes : inviteCodes;

  return (
    <div className="max-w-4xl mx-auto my-4 sm:my-10 p-4 sm:p-6 rounded-lg shadow-md bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle text-text-primary-light dark:text-text-primary min-w-0">
      <h1 className="text-2xl font-bold mb-6 text-center">Admin Panel</h1>

      <UserStatsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        loading={loading}
        error={error}
        stats={stats}
      />

      <div className="flex border-b border-border-subtle-light dark:border-border-subtle mb-6 overflow-x-auto">
        <button
          type="button"
          onClick={() => setActiveTab("queries")}
          className={`py-3 px-6 font-semibold text-sm border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "queries"
              ? "border-blue-500 text-blue-600 dark:text-blue-400"
              : "border-transparent text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary"
          }`}
        >
          User Queries
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("outreach")}
          className={`py-3 px-6 font-semibold text-sm border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "outreach"
              ? "border-blue-500 text-blue-600 dark:text-blue-400"
              : "border-transparent text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary"
          }`}
        >
          Outreach
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("user-update")}
          className={`py-3 px-6 font-semibold text-sm border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "user-update"
              ? "border-blue-500 text-blue-600 dark:text-blue-400"
              : "border-transparent text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary"
          }`}
        >
          Trigger User Update
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("promo-codes")}
          className={`py-3 px-6 font-semibold text-sm border-b-2 transition-colors whitespace-nowrap ${
            activeTab === "promo-codes"
              ? "border-blue-500 text-blue-600 dark:text-blue-400"
              : "border-transparent text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary"
          }`}
        >
          Promo Codes ({inviteCodes.filter((c) => !c.usedAt).length} Unused)
        </button>
      </div>

      {activeTab === "user-update" && (
        <div className="p-5 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-default-light dark:border-border-default space-y-4">
          <div>
            <h2 className="text-lg font-bold mb-1">
              Trigger Community User Update
            </h2>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary">
              Provide release notes, new feature announcements, or product
              updates. The Builder AI will craft concise, accurate updates and
              schedule them to the selected user groups.
            </p>
          </div>

          <form onSubmit={handleSendUserUpdate} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold mb-2 text-text-secondary-light dark:text-text-secondary">
                Target Groups
              </label>
              <div className="space-y-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={sendToHebrewGroup}
                    onChange={(e) =>
                      setSendToHebrewGroup(
                        (e.target as HTMLInputElement).checked,
                      )}
                    disabled={updateLoading}
                    className="rounded border-border-subtle text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    WhatsApp Hebrew Group (
                    <span className="font-mono text-xs text-text-secondary-light dark:text-text-secondary">
                      prompt2bot users - Hebrew
                    </span>
                    )
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={sendToEnglishGroup}
                    onChange={(e) =>
                      setSendToEnglishGroup(
                        (e.target as HTMLInputElement).checked,
                      )}
                    disabled={updateLoading}
                    className="rounded border-border-subtle text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    WhatsApp English Group (
                    <span className="font-mono text-xs text-text-secondary-light dark:text-text-secondary">
                      prompt2bot users
                    </span>
                    )
                  </span>
                </label>
                <label className="flex items-center gap-2 opacity-60 cursor-not-allowed select-none">
                  <input
                    type="checkbox"
                    disabled
                    checked={false}
                    className="rounded border-border-subtle text-gray-400"
                  />
                  <span>Discord Community</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle text-text-tertiary-light dark:text-text-tertiary">
                    Coming soon
                  </span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold mb-1 text-text-secondary-light dark:text-text-secondary">
                Update Content / Release Notes
              </label>
              <textarea
                value={updateContext}
                onInput={(e) =>
                  setUpdateContext((e.target as HTMLTextAreaElement).value)}
                placeholder="e.g. We just released audio calls and voice messages! Bots can now participate in WebRTC live voice sessions and handle WhatsApp voice notes smoothly."
                rows={5}
                className={`w-full ${inputBaseClass} py-2`}
                disabled={updateLoading}
                required
              />
            </div>

            {updateError && (
              <div className="p-3 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-xs border border-red-200 dark:border-red-800">
                {updateError}
              </div>
            )}

            {updateSuccess && (
              <div className="p-3 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs border border-green-200 dark:border-green-800 font-mono">
                {updateSuccess}
              </div>
            )}

            <Button
              type="submit"
              disabled={updateLoading || !updateContext.trim() ||
                (!sendToHebrewGroup && !sendToEnglishGroup)}
            >
              {updateLoading
                ? "Publishing Update..."
                : "Publish User Update via Builder AI"}
            </Button>
          </form>
        </div>
      )}

      {activeTab === "outreach" && (
        <div className="space-y-6">
          <div className="p-5 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-default-light dark:border-border-default">
            <h2 className="text-lg font-bold mb-1">
              Agent Outreach API & Constant Token
            </h2>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary mb-4">
              Use this constant token to initiate WhatsApp Builder AI outreach
              from remote code, external scripts, or autonomous agents.
            </p>

            {outreachTokenLoading && (
              <div className="flex items-center gap-2 py-4 text-xs text-text-secondary-light dark:text-text-secondary">
                <Spinner />
                <span>Loading outreach token...</span>
              </div>
            )}

            {outreachTokenError && (
              <div className="p-3 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-xs border border-red-200 dark:border-red-800">
                {outreachTokenError}
              </div>
            )}

            {outreachToken && (
              <div className="space-y-5">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-text-secondary-light dark:text-text-secondary">
                    Constant Outreach Token
                  </label>
                  <TokenDisplay
                    token={outreachToken}
                    label="Token"
                  />
                </div>

                <div className="border-t border-border-subtle-light dark:border-border-subtle pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-xs font-semibold text-text-secondary-light dark:text-text-secondary">
                        Instructions to Throw on an Agent
                      </div>
                      <div className="text-[11px] text-text-tertiary-light dark:text-text-tertiary">
                        Copy and paste directly into your external agent's
                        prompt or instructions (token included).
                      </div>
                    </div>
                    <CopyButton
                      text={agentOutreachInstructions({
                        token: outreachToken,
                        apiUrl,
                      })}
                    />
                  </div>
                  <pre className="p-3 rounded bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle text-xs font-mono text-text-primary-light dark:text-text-primary whitespace-pre-wrap overflow-x-auto">
                    {agentOutreachInstructions({
                      token: outreachToken,
                      apiUrl,
                    })}
                  </pre>
                </div>

                <div className="border-t border-border-subtle-light dark:border-border-subtle pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-text-secondary-light dark:text-text-secondary">
                      cURL Example
                    </div>
                    <CopyButton
                      text={curlOutreachExample({
                        token: outreachToken,
                        apiUrl,
                      })}
                    />
                  </div>
                  <CodeBlock
                    code={curlOutreachExample({
                      token: outreachToken,
                      apiUrl,
                    })}
                    lang="bash"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="p-5 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-default-light dark:border-border-default">
            <h2 className="text-lg font-bold mb-2">
              Manual Outreach Test (WhatsApp)
            </h2>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary mb-4">
              Provide context and a phone number to instruct the Builder AI to
              reach out on WhatsApp and start building an agent with them.
            </p>

            <form onSubmit={handleSendOutreach} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-text-secondary-light dark:text-text-secondary">
                    Phone Number *
                  </label>
                  <input
                    type="text"
                    value={outreachPhone}
                    onInput={(e) =>
                      setOutreachPhone((e.target as HTMLInputElement).value)}
                    placeholder="e.g. +972521234567 or +14155552671"
                    className={`w-full ${inputBaseClass}`}
                    disabled={outreachLoading}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-text-secondary-light dark:text-text-secondary">
                    Prospect Name
                  </label>
                  <input
                    type="text"
                    value={outreachName}
                    onInput={(e) =>
                      setOutreachName((e.target as HTMLInputElement).value)}
                    placeholder="e.g. דרור בוקצ'ין or Alex"
                    className={`w-full ${inputBaseClass}`}
                    disabled={outreachLoading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold mb-1 text-text-secondary-light dark:text-text-secondary">
                    Language
                  </label>
                  <div className="flex gap-4 mt-2">
                    <label className="flex items-center gap-1 text-xs cursor-pointer">
                      <input
                        type="radio"
                        name="outreachLang"
                        checked={outreachLanguage === "hebrew"}
                        onChange={() => setOutreachLanguage("hebrew")}
                        disabled={outreachLoading}
                      />
                      <span>עברית (Hebrew)</span>
                    </label>
                    <label className="flex items-center gap-1 text-xs cursor-pointer">
                      <input
                        type="radio"
                        name="outreachLang"
                        checked={outreachLanguage === "english"}
                        onChange={() => setOutreachLanguage("english")}
                        disabled={outreachLoading}
                      />
                      <span>English</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold mb-1 text-text-secondary-light dark:text-text-secondary">
                    Lead Source
                  </label>
                  <input
                    type="text"
                    value={outreachSource}
                    onInput={(e) =>
                      setOutreachSource((e.target as HTMLInputElement).value)}
                    placeholder="e.g. קבוצת talkingautomation בפייסבוק or Reddit r/n8n"
                    className={`w-full ${inputBaseClass}`}
                    disabled={outreachLoading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1 text-text-secondary-light dark:text-text-secondary">
                  Business Need / Use Case *
                </label>
                <textarea
                  value={outreachUseCase}
                  onInput={(e) =>
                    setOutreachUseCase((e.target as HTMLTextAreaElement).value)}
                  placeholder="e.g. מעוניין באוטומציה שקולטת הודעות וואטסאפ על האכלות וטיפולים וטרינריים ל-75 סוסים בפנסיון ומתעדת ב-Google Sheets"
                  rows={3}
                  className={`w-full ${inputBaseClass} py-2`}
                  disabled={outreachLoading}
                  required={!outreachContext.trim()}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold mb-1 text-text-secondary-light dark:text-text-secondary">
                  Additional Notes / Context
                </label>
                <textarea
                  value={outreachContext}
                  onInput={(e) =>
                    setOutreachContext((e.target as HTMLTextAreaElement).value)}
                  placeholder="e.g. פרטים נוספים או דגשים מיוחדים"
                  rows={2}
                  className={`w-full ${inputBaseClass} py-2`}
                  disabled={outreachLoading}
                />
              </div>

              {outreachError && (
                <div className="p-3 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-xs border border-red-200 dark:border-red-800">
                  {outreachError}
                </div>
              )}

              {outreachSuccess && (
                <div className="p-3 rounded bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 text-xs border border-green-200 dark:border-green-800 font-mono">
                  {outreachSuccess}
                </div>
              )}

              <Button
                type="submit"
                disabled={outreachLoading || !outreachPhone.trim() ||
                  (!outreachUseCase.trim() && !outreachContext.trim())}
              >
                {outreachLoading ? "Sending..." : "Send Builder AI"}
              </Button>
            </form>
          </div>
        </div>
      )}

      {activeTab === "queries" && (
        <div className="space-y-8">
          <div>
            <h2 className="text-xl font-bold mb-4">User Stats Lookup</h2>

            <form
              onSubmit={handleSearch}
              className="flex flex-col sm:flex-row gap-2 mb-6"
            >
              <input
                type="text"
                value={query}
                onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
                placeholder="Enter user email, phone number, or Telegram ID"
                className={`flex-1 min-w-0 ${inputBaseClass}`}
                disabled={loading}
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? "Searching..." : "Search"}
              </Button>
            </form>
          </div>

          <div className="border-t border-border-subtle-light dark:border-border-subtle pt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div>
                <h2 className="text-xl font-bold">Users Directory</h2>
                <p className="text-xs text-text-secondary-light dark:text-text-secondary mt-1">
                  Showing paying users and users with active referrals (live
                  users) first.
                </p>
              </div>

              <input
                type="text"
                value={tableFilter}
                onInput={(e) => {
                  setTableFilter((e.target as HTMLInputElement).value);
                  setPage(1);
                }}
                placeholder="Filter table..."
                className={`w-full md:w-64 ${inputBaseClass} text-xs`}
              />
            </div>

            {usersLoading && (
              <div className="p-8 flex flex-col items-center justify-center gap-3 text-sm text-text-secondary-light dark:text-text-secondary">
                <Spinner className="h-8 w-8 text-blue-500" />
                <span>Loading users table...</span>
              </div>
            )}

            {usersError && (
              <div className="p-4 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 text-sm border border-red-200 dark:border-red-800 mb-4">
                {usersError}
              </div>
            )}

            {!usersLoading && !usersError && (
              <div className="rounded-lg border border-border-default-light dark:border-border-default overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-surface-overlay-light dark:bg-surface-overlay text-text-secondary-light dark:text-text-secondary text-xs uppercase font-semibold border-b border-border-subtle-light dark:border-border-subtle">
                      <tr>
                        <TableHeaderCell
                          label="User"
                          field="user"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <TableHeaderCell
                          label="Tier"
                          field="tier"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <TableHeaderCell
                          label="Ref Signups"
                          field="refSignups"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <TableHeaderCell
                          label="Ref Earnings"
                          field="refEarnings"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <TableHeaderCell
                          label="Bots"
                          field="botsCount"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <TableHeaderCell
                          label="Last Active"
                          field="lastActive"
                          sortField={sortField}
                          sortDirection={sortDirection}
                          onSort={handleSort}
                        />
                        <th className="p-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-subtle-light dark:divide-border-subtle">
                      {paginatedUsers.length === 0
                        ? (
                          <tr>
                            <td
                              colSpan={7}
                              className="p-4 text-center text-xs text-text-secondary-light dark:text-text-secondary"
                            >
                              No users found.
                            </td>
                          </tr>
                        )
                        : (
                          paginatedUsers.map((u) => {
                            const userLabel = getUserLabel(u);
                            return (
                              <tr
                                key={u.id}
                                className="hover:bg-surface-overlay-light/50 dark:hover:bg-surface-overlay/50 transition-colors"
                              >
                                <td className="p-3 font-mono text-xs select-all">
                                  <div className="font-semibold text-text-primary-light dark:text-text-primary">
                                    {userLabel}
                                  </div>
                                  {u.email && u.phone && (
                                    <div className="text-text-tertiary-light dark:text-text-tertiary">
                                      {u.phone}
                                    </div>
                                  )}
                                </td>
                                <td className="p-3">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded font-medium ${
                                        u.isPaid
                                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                                          : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"
                                      }`}
                                    >
                                      {u.tier}
                                    </span>
                                    {u.isPaid && (
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 font-bold">
                                        PAYING
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-3 text-xs font-mono">
                                  {u.refStats.signupsCount > 0
                                    ? (
                                      <span className="text-green-600 dark:text-green-400 font-bold">
                                        {u.refStats.signupsCount} signups
                                        {u.refStats.conversionsCount > 0 &&
                                          ` (${u.refStats.conversionsCount} paying)`}
                                      </span>
                                    )
                                    : (
                                      <span className="text-text-tertiary-light dark:text-text-tertiary">
                                        —
                                      </span>
                                    )}
                                </td>
                                <td className="p-3 text-xs font-mono">
                                  {u.refStats.visitsCount > 0 ||
                                      u.refStats.earnings > 0
                                    ? (
                                      <span>
                                        {u.refStats.visitsCount}{" "}
                                        visits | ${u.refStats.earnings}/mo
                                      </span>
                                    )
                                    : (
                                      <span className="text-text-tertiary-light dark:text-text-tertiary">
                                        —
                                      </span>
                                    )}
                                </td>
                                <td className="p-3 font-mono text-xs">
                                  {u.botsCount}
                                </td>
                                <td className="p-3 text-xs text-text-secondary-light dark:text-text-secondary font-mono">
                                  {u.lastVisit || u.signupTimestamp
                                    ? new Date(
                                      u.lastVisit || u.signupTimestamp!,
                                    ).toLocaleDateString()
                                    : "—"}
                                </td>
                                <td className="p-3 text-right">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const queryVal = u.email || u.phone ||
                                        u.telegramId || u.id;
                                      setQuery(queryVal);
                                      setIsModalOpen(true);
                                      lookupUserByQuery(queryVal);
                                    }}
                                    className="text-xs px-2.5 py-1 rounded bg-surface-overlay-light hover:bg-surface-base-light dark:bg-surface-overlay dark:hover:bg-surface-base border border-border-default-light dark:border-border-default font-semibold transition-colors cursor-pointer"
                                  >
                                    View Stats
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-border-subtle-light dark:border-border-subtle text-xs text-text-secondary-light dark:text-text-secondary bg-surface-overlay-light/30 dark:bg-surface-overlay/30">
                  <div className="flex items-center gap-3">
                    <span>
                      Showing {sortedUsers.length === 0
                        ? 0
                        : (currentPage - 1) * pageSize + 1} to{" "}
                      {Math.min(currentPage * pageSize, sortedUsers.length)} of
                      {" "}
                      {sortedUsers.length} users
                    </span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(
                          Number((e.target as HTMLSelectElement).value),
                        );
                        setPage(1);
                      }}
                      className={`px-2 py-1 rounded ${inputBaseClass} text-xs`}
                    >
                      <option value={10}>10 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="px-2.5 py-1 rounded border border-border-default-light dark:border-border-default bg-surface-overlay-light dark:bg-surface-overlay disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-base transition-colors cursor-pointer"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1 font-mono">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))}
                      className="px-2.5 py-1 rounded border border-border-default-light dark:border-border-default bg-surface-overlay-light dark:bg-surface-overlay disabled:opacity-40 disabled:cursor-not-allowed hover:bg-surface-base transition-colors cursor-pointer"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "promo-codes" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-lg bg-surface-overlay-light dark:bg-surface-overlay border border-border-default-light dark:border-border-default">
            <div>
              <h2 className="text-lg font-bold mb-1">
                Mentat Pre-Launch Promo Codes
              </h2>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary">
                Each code provides 1 dedicated WhatsApp phone number with 1st
                month free ($0) on PBXME.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                onClick={fetchInviteCodes}
                disabled={inviteCodesLoading}
                className="text-xs"
              >
                {inviteCodesLoading ? "Refreshing..." : "Refresh"}
              </Button>
              {unusedCodes.length > 0 && (
                <Button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      unusedCodes.map((c) => c.code).join("\n"),
                    );
                    toast.success(
                      `Copied ${unusedCodes.length} unused codes!`,
                    );
                  }}
                  className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Copy All Unused ({unusedCodes.length})
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl border border-border-default-light dark:border-border-default bg-surface-raised-light dark:bg-surface-raised">
              <span className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
                Total Codes
              </span>
              <div className="text-2xl font-bold mt-1">
                {inviteCodes.length}
              </div>
            </div>
            <div className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5">
              <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                Unused / Available
              </span>
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                {unusedCodes.length}
              </div>
            </div>
            <div className="p-4 rounded-xl border border-border-default-light dark:border-border-default bg-surface-raised-light dark:bg-surface-raised">
              <span className="text-xs text-text-secondary-light dark:text-text-secondary font-medium">
                Redeemed
              </span>
              <div className="text-2xl font-bold text-text-primary-light dark:text-text-primary mt-1">
                {redeemedCodes.length}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs font-semibold text-text-primary-light dark:text-text-primary cursor-pointer">
              <input
                type="checkbox"
                checked={showUnusedOnly}
                onChange={(e) =>
                  setShowUnusedOnly((e.target as HTMLInputElement).checked)}
                className="rounded border-border-default-light dark:border-border-default"
              />
              Show Unused Only
            </label>
          </div>

          {inviteCodesLoading
            ? (
              <div className="flex justify-center p-8">
                <Spinner className="w-8 h-8 text-accent" />
              </div>
            )
            : inviteCodesError
            ? (
              <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl text-xs text-rose-600">
                {inviteCodesError}
              </div>
            )
            : displayedCodes.length === 0
            ? (
              <div className="p-8 text-center text-xs text-text-secondary-light dark:text-text-secondary">
                No promo codes found.
              </div>
            )
            : (
              <div className="overflow-x-auto rounded-lg border border-border-default-light dark:border-border-default">
                <table className="min-w-full divide-y divide-border-subtle-light dark:divide-border-subtle text-xs">
                  <thead className="bg-surface-overlay-light dark:bg-surface-overlay text-text-secondary-light dark:text-text-secondary">
                    <tr>
                      <th className="py-2.5 px-4 text-left font-semibold">
                        Promo Code
                      </th>
                      <th className="py-2.5 px-4 text-left font-semibold">
                        Status
                      </th>
                      <th className="py-2.5 px-4 text-left font-semibold">
                        Source
                      </th>
                      <th className="py-2.5 px-4 text-left font-semibold">
                        Redeemed By
                      </th>
                      <th className="py-2.5 px-4 text-left font-semibold">
                        Created Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle-light dark:divide-border-subtle bg-surface-raised-light dark:bg-surface-raised">
                    {displayedCodes.map((c) => (
                      <tr key={c.id}>
                        <td className="py-2.5 px-4 font-mono font-bold text-text-primary-light dark:text-text-primary flex items-center gap-2">
                          <span>{c.code}</span>
                          <CopyButton text={c.code} />
                        </td>
                        <td className="py-2.5 px-4">
                          {c.usedAt
                            ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500/10 text-rose-600">
                                Used
                              </span>
                            )
                            : (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-600">
                                Available
                              </span>
                            )}
                        </td>
                        <td className="py-2.5 px-4 text-text-secondary-light dark:text-text-secondary">
                          {c.generatedByEmail
                            ? `User: ${c.generatedByEmail}`
                            : "Seed / Admin"}
                        </td>
                        <td className="py-2.5 px-4 font-mono text-text-secondary-light dark:text-text-secondary">
                          {c.usedByEmail || "—"}
                        </td>
                        <td className="py-2.5 px-4 text-text-secondary-light dark:text-text-secondary whitespace-nowrap">
                          {new Date(c.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}
    </div>
  );
};
